import type { PopperPlacementType } from '@mui/material';
import type {
  DropdownMenu2OriginProp,
  DropdownMenu2Placement,
  DropdownMenu2PlacementDetail,
  DropdownMenuHorizontalOrigin,
  DropdownMenuOrigin,
  DropdownMenuVerticalOrigin,
} from './types';

// ============================================================================
// Types
// ============================================================================

export interface OriginObject {
  horizontal: DropdownMenuHorizontalOrigin;
  vertical: DropdownMenuVerticalOrigin;
}

export interface ResolvedPlacement {
  /**
   * Popper placement 값
   */
  popperPlacement: PopperPlacementType;
  /**
   * Transform origin { vertical, horizontal }
   */
  transformOrigin: OriginObject;
}

// ============================================================================
// Mapping Tables
// ============================================================================

/**
 * Popper placement → anchorOrigin 매핑
 */
const placementToAnchorOrigin: Record<PopperPlacementType, DropdownMenuOrigin> = {
  'bottom-start': 'bottom left',
  bottom: 'bottom center',
  'bottom-end': 'bottom right',
  'top-start': 'top left',
  top: 'top center',
  'top-end': 'top right',
  'left-start': 'top left',
  left: 'center left',
  'left-end': 'bottom left',
  'right-start': 'top right',
  right: 'center right',
  'right-end': 'bottom right',
  auto: 'bottom left',
  'auto-start': 'bottom left',
  'auto-end': 'bottom right',
};

/**
 * anchorOrigin → Popper placement 매핑
 */
const anchorOriginToPlacement: Record<DropdownMenuOrigin, PopperPlacementType> = {
  'bottom left': 'bottom-start',
  'bottom center': 'bottom',
  'bottom right': 'bottom-end',
  'top left': 'top-start',
  'top center': 'top',
  'top right': 'top-end',
  'center left': 'left',
  'center center': 'bottom', // fallback
  'center right': 'right',
};

/**
 * anchorOrigin → 기본 transformOrigin 자동 계산 매핑
 * (DropdownMenu의 defaultOriginMapping 재사용)
 */
const defaultTransformOriginMapping: Record<DropdownMenuOrigin, DropdownMenuOrigin> = {
  'top left': 'bottom left',
  'top center': 'bottom center',
  'top right': 'bottom right',
  'center left': 'center right',
  'center center': 'top left',
  'center right': 'center left',
  'bottom left': 'top left',
  'bottom center': 'top center',
  'bottom right': 'top right',
};

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Origin 문자열을 파싱합니다.
 */
function parseOriginString(origin: DropdownMenuOrigin): OriginObject {
  const [vertical, horizontal] = origin.split(' ') as [
    DropdownMenuVerticalOrigin,
    DropdownMenuHorizontalOrigin,
  ];
  return { vertical, horizontal };
}

/**
 * placement가 shorthand(문자열)인지 확인합니다.
 */
function isPlacementShorthand(placement: DropdownMenu2Placement): placement is PopperPlacementType {
  return typeof placement === 'string';
}

/**
 * placement가 detail(객체)인지 확인합니다.
 */
function isPlacementDetail(
  placement: DropdownMenu2Placement,
): placement is DropdownMenu2PlacementDetail {
  return typeof placement === 'object' && 'anchorOrigin' in placement;
}

// ============================================================================
// Main Functions
// ============================================================================

/**
 * Placement를 해석하여 popperPlacement와 transformOrigin을 반환합니다.
 *
 * @param placement - shorthand 또는 detail 형식의 placement
 * @returns { popperPlacement, transformOrigin }
 */
export function resolvePlacement(placement: DropdownMenu2Placement): ResolvedPlacement {
  if (isPlacementShorthand(placement)) {
    // Shorthand: placement 문자열 → anchorOrigin → transformOrigin 자동 계산
    const anchorOrigin = placementToAnchorOrigin[placement] ?? 'bottom left';
    const transformOrigin = defaultTransformOriginMapping[anchorOrigin];

    return {
      popperPlacement: placement,
      transformOrigin: parseOriginString(transformOrigin),
    };
  }

  if (isPlacementDetail(placement)) {
    // Detail: anchorOrigin에서 placement 역계산, transformOrigin 자동 계산 또는 명시값 사용
    const { anchorOrigin, transformOrigin: explicitTransformOrigin } = placement;
    const popperPlacement = anchorOriginToPlacement[anchorOrigin] ?? 'bottom-start';
    const transformOrigin =
      explicitTransformOrigin ?? defaultTransformOriginMapping[anchorOrigin] ?? 'top left';

    return {
      popperPlacement,
      transformOrigin: parseOriginString(transformOrigin),
    };
  }

  // Fallback
  return {
    popperPlacement: 'bottom-start',
    transformOrigin: { vertical: 'top', horizontal: 'left' },
  };
}

/**
 * Deprecated origin prop을 placement로 변환합니다.
 *
 * @param origin - DropdownMenu 스타일의 origin prop
 * @returns DropdownMenu2Placement
 */
export function convertOriginToPlacement(origin: DropdownMenu2OriginProp): DropdownMenu2Placement {
  if (typeof origin === 'string') {
    // Shorthand origin: "bottom left" → placement detail로 변환
    return { anchorOrigin: origin };
  }

  // Detail origin: { anchor, paper } → { anchorOrigin, transformOrigin }
  const { anchor, paper } = origin;
  return {
    anchorOrigin: anchor ?? 'bottom left',
    transformOrigin: paper,
  };
}
